import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams, useLocation } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { GET_MAIN_CATEGORIES } from '../apollo/queries';
import { GET_SYSTEM_SETTINGS } from '../apollo/queries/settingsQueries';
import { GET_MY_CART } from '../apollo/queries/cartQueries';
import './css/Header.css';
import { useAuth } from '../admin/AuthContext';

const Header = ({ onLoginClick }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const { user, logout, isAuthenticated } = useAuth();
    const [isMobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [isScrolled, setIsScrolled] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [isCategoryPopupOpen, setCategoryPopupOpen] = useState(false);
    
    // GraphQL 쿼리 실행
    const { data: categoriesData } = useQuery(GET_MAIN_CATEGORIES);
    const { data: settingsData } = useQuery(GET_SYSTEM_SETTINGS, {
        errorPolicy: 'ignore',
        fetchPolicy: 'cache-and-network',
        notifyOnNetworkStatusChange: true
    });

    // 장바구니 데이터 가져오기 (로그인한 사용자만)
    const { data: cartData } = useQuery(GET_MY_CART, {
        skip: !isAuthenticated(),
        errorPolicy: 'ignore',
        fetchPolicy: 'cache-and-network'
    });

    // 장바구니 총 수량 계산
    const cartItemCount = cartData?.myCart?.reduce((total, item) => total + item.quantity, 0) || 0;

    // 현재 활성 카테고리 확인
    const currentCategoryId = searchParams.get('category');
    const currentMainCategoryId = searchParams.get('mainCategory');
    const typeParam = searchParams.get('type');
    
    // type=sub인 경우 mainCategory를, 그렇지 않으면 category를 활성 메인 카테고리로 사용
    const activeMainCategoryId = typeParam === 'sub' ? currentMainCategoryId : currentCategoryId;

    const handleCartClick = () => {
        if (!isAuthenticated()) {
            // 로그인 팝업 열기
            window.dispatchEvent(new CustomEvent('openLoginPanel'));
            return;
        }
        navigate('/cart');
    };

    const toggleMobileMenu = () => {
        setMobileMenuOpen(!isMobileMenuOpen);
    };

    const toggleCategoryPopup = () => {
        setCategoryPopupOpen(!isCategoryPopupOpen);
    };

    const handleCategoryClick = (categoryId) => {
        navigate(`/shop?category=${categoryId}`);
        setCategoryPopupOpen(false);
    };

    // 검색 핸들러
    const handleSearch = (e) => {
        e.preventDefault();
        if (searchQuery.trim()) {
            navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
        }
    };

    // 검색 버튼 클릭 핸들러
    const handleSearchButtonClick = () => {
        if (searchQuery.trim()) {
            navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
        }
    };

    // 검색 입력 키 핸들러
    const handleSearchKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleSearch(e);
        }
    };

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 0);
            // 스크롤 시 카테고리 팝업 닫기
            if (isCategoryPopupOpen) {
                setCategoryPopupOpen(false);
            }
        };

        const handleClickOutside = (e) => {
            // 카테고리 팝업 외부 클릭 시 닫기
            if (isCategoryPopupOpen && !e.target.closest('.category-popup') && !e.target.closest('.all-products-btn')) {
                setCategoryPopupOpen(false);
            }
        };

        window.addEventListener('scroll', handleScroll);
        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            window.removeEventListener('scroll', handleScroll);
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isCategoryPopupOpen]);

    return (
        <>
            <header className={`shop-header ${isScrolled ? 'scrolled' : ''}`}>
                <div className="top-bar">
                    <div className="logo-container">
                        <Link to="/">
                            <img src="/logo.png" alt="Busbar World Logo" className="logo" />
                        </Link>
                    </div>
                    <div className="search-container">
                        <form onSubmit={handleSearch} className="search-form">
                            <input 
                                className="search-input" 
                                type="text" 
                                placeholder="검색어를 입력하세요"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                onKeyPress={handleSearchKeyPress}
                            />
                            <button 
                                type="button" 
                                className="search-button"
                                onClick={handleSearchButtonClick}
                            >
                                {/* Search Icon */}
                                <img src="/frame-17074819070.svg" alt="Search" className="search-icon" />
                            </button>
                        </form>
                    </div>

                    {/* Desktop side actions */}
                    <div className="side-actions">
                        <div className="contact-info">
                             <span className="support-icon">
                                {/* Support agent icon */}
                                <img src="/support-agent0.svg" alt="Support" className="support-icon-img" />
                            </span>
                            <div>
                                <p>{settingsData?.getSystemSettings?.businessHours || '고객센터 10:00~17:00'}</p>
                                <p className="phone-number">{settingsData?.getSystemSettings?.phone || '031-479-1630'}</p>
                                <p>{settingsData?.getSystemSettings?.email || 'info@busbarworld.com'}</p>
                            </div>
                        </div>
                        <div className="user-actions">
                            <div className="action-item" onClick={handleCartClick}>
                               <div className="icon-wrapper">
                                    {/* cart icon */}
                                    <img src="/shopping-cart1.svg" alt="Cart" className="cart-icon" />
                                    {cartItemCount > 0 && <span className="cart-count">{cartItemCount}</span>}
                               </div>
                                <p>장바구니</p>
                            </div>
                            <div className="action-item" onClick={() => {
                                console.log('My Info button clicked in Header');
                                onLoginClick();
                            }}>
                                <span className="icon-wrapper">
                                    {/* user icon */}
                                    <img src="/person-41.svg" alt="User" className="user-icon" />
                                </span>
                                <p>{isAuthenticated() ? '내 정보' : '로그인'}</p>
                            </div>
                        </div>
                    </div>
                    
                    {/* Mobile header actions */}
                    <div className="mobile-header-actions">
                        <div className="action-item" onClick={handleCartClick}>
                            <div className="icon-wrapper">
                                <img src="/shopping-cart1.svg" alt="Cart" className="cart-icon" />
                            </div>
                        </div>
                        <div className="action-item" onClick={() => { onLoginClick(); }}>
                            <span className="icon-wrapper">
                                <img src="/person-41.svg" alt="User" className="user-icon" />
                            </span>
                        </div>
                        <button className="mobile-menu-icon" onClick={toggleMobileMenu}>
                            <img src="/more-vert-icon.svg" alt="Menu" className="more-vert-icon" />
                        </button>
                    </div>
                </div>
                <nav className="bottom-nav">
                    <div className="bottom-nav-inner">
                        <div className="nav-links">
                            <a href="/" className={`nav-home-link ${location.pathname === '/' ? 'active' : ''}`}>
                                {/* home icon */}
                                <img src="/home0.svg" alt="Home" className="home-icon" />
                            </a>
                            {categoriesData?.mainCategories?.filter(cat => cat.isActive).map((category) => (
                                <a 
                                    key={category.id} 
                                    href={`/shop?category=${category.id}`} 
                                    className={`nav-link ${activeMainCategoryId === category.id ? 'active' : ''}`}
                                >
                                    {category.name}
                                </a>
                            ))}
                            <a 
                                href="/custom-order" 
                                className={`nav-link ${location.pathname === '/custom-order' ? 'active' : ''}`}
                            >
                                주문 제작
                            </a>
                        </div>
                        <button className="all-products-btn" onClick={toggleCategoryPopup}>
                            전체보기
                            {/* arrow down icon */}
                            <img src="/keyboard-arrow-down0.svg" alt="Arrow Down" className="arrow-down-icon" />
                        </button>
                    </div>
                </nav>

                {/* Category Popup */}
                {isCategoryPopupOpen && (
                    <>
                        <div key="category-overlay" className="category-popup-overlay" onClick={() => setCategoryPopupOpen(false)}></div>
                        <div key="category-popup" className="category-popup">
                            <div className="category-grid-container">
                                <div className={`category-grid ${categoriesData?.mainCategories?.filter(cat => cat.isActive).length > 6 ? 'many-categories' : 'few-categories'}`}>
                                    {categoriesData?.mainCategories?.filter(cat => cat.isActive).map((category, index) => (
                                        <div 
                                            key={category.id} 
                                            className={`category-item ${activeMainCategoryId === category.id ? 'active' : ''}`}
                                            onClick={() => handleCategoryClick(category.id)}
                                        >
                                            <div className="category-content">
                                                <h3>{category.name}</h3>
                                                <div className="category-details">
                                                    <div className="category-label">전체보기</div>
                                                    {/* 실제 하위 카테고리 표시 */}
                                                    <div className="category-items">
                                                        {category.subCategories && category.subCategories.length > 0 ? (
                                                            category.subCategories
                                                                .filter(sub => sub.isActive)
                                                                .slice(0, 6)
                                                                .map(subCategory => (
                                                                    <div 
                                                                        key={subCategory.id} 
                                                                        className="category-spec"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            navigate(`/shop?category=${subCategory.id}&type=sub&mainCategory=${category.id}`);
                                                                            setCategoryPopupOpen(false);
                                                                        }}
                                                                    >
                                                                        {subCategory.name}
                                                                        {subCategory.productCount > 0 && (
                                                                            <span className="product-count"> ({subCategory.productCount})</span>
                                                                        )}
                                                                    </div>
                                                                ))
                                                        ) : (
                                                            <div className="category-spec">
                                                                {category.productCount > 0 
                                                                    ? `${category.productCount}개 제품` 
                                                                    : '등록된 제품 없음'
                                                                }
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </>
                )}

                {isMobileMenuOpen && <div key="mobile-overlay" className="mobile-menu-overlay" onClick={toggleMobileMenu}></div>}

                <div className={`mobile-side-menu ${isMobileMenuOpen ? 'open' : ''}`}>
                    <button className="close-menu-btn" onClick={toggleMobileMenu}>X</button>
                    {categoriesData?.mainCategories?.filter(cat => cat.isActive).map((category) => (
                        <a key={category.id} href={`/shop?category=${category.id}`} className="nav-link">
                            {category.name}
                        </a>
                    ))}
                    <a href="/custom-order" className="nav-link">
                        주문 제작
                    </a>
                </div>
            </header>
            {isScrolled && <div key="header-placeholder" className="header-placeholder"></div>}
        </>
    );
};

export default Header; 